import {
  BaseApplicationService,
  SurveyEntity,
  SurveyAnswer,
  surveyApiClient,
  SurveyTabState,
  SmartApplyApiError,
  reportUnexpectedError,
  DefaultNumItemsPerPages,
  toTotalPage,
  rebuildQuery,
  ToastMessage,
  nameCsvFileWithTerm,
  csvMetaFields,
  generateZip,
  isLGWANURL,
} from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { SurveyPublishSetting } from "@graffer-inc/cloud-gov-core";
import FileSaver from "file-saver";
import { SurveyR } from "../../../../components/pages/surveys/route";
import {
  SurveyAnswerTableBody,
  SurveyAnswerTableHeader,
  generateSurveyAnswerTable,
} from "../generator/survey-answer-table-generator";
import { generateAttachedFilesFolder } from "../generator/attached-files-folder-generator";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import { CSVEncoding } from "../../../domain/file-encoding";
import type { SurveyCsvMetaID } from "@graffer-inc/smart-apply/dist/application/domain/surveys/csv-meta-field";
import { formatDate } from "../../../../utils/date-utils";

export type SurveyDetailServiceProps = {
  survey?: SurveyEntity;
  stats?: {
    numOfTotalAnswer: number;
    numOfRecent24HoursAnswers: number;
  };
  tabState: SurveyTabState;
  switchTab: typeof SurveyDetailService.prototype.switchTab;
  results: SurveyAnswer[];
  tableData: typeof SurveyDetailService.prototype.tableData;
  setDateRange: typeof SurveyDetailService.prototype.setDateRange;
  dateRange: {
    from?: Date;
    to?: Date;
  };
  paging: {
    current: number;
    total: number;
  };
  selectedMetaIds: SurveyCsvMetaID[];
  requireSelectableRawValue: boolean;
  onMount: typeof SurveyDetailService.prototype.onMount;
  onSave: typeof SurveyDetailService.prototype.onSave;
  onDelete: typeof SurveyDetailService.prototype.onDelete;
  onUnPublish: typeof SurveyDetailService.prototype.onUnPublish;
  onResumeAccepting: typeof SurveyDetailService.prototype.onResumeAccepting;
  onPauseAccepting: typeof SurveyDetailService.prototype.onPauseAccepting;
  onPublishSettingSave: typeof SurveyDetailService.prototype.onPublishSettingSave;
  onResultsRequested: typeof SurveyDetailService.prototype.onResultsRequested;
  onCSVDownload: typeof SurveyDetailService.prototype.onCSVDownload;
  onGroupUpdated: typeof SurveyDetailService.prototype.onGroupUpdated;
  onReturnToDraft: typeof SurveyDetailService.prototype.onReturnToDraft;
  onCopy: typeof SurveyDetailService.prototype.onCopy;
  onExportSurvey: typeof SurveyDetailService.prototype.onExportSurvey;
  setPage: typeof SurveyDetailService.prototype.setPage;
  movePage: typeof SurveyDetailService.prototype.movePage;
  onSelectCsvMeta: typeof SurveyDetailService.prototype.onSelectCsvMeta;
  onChangeRequireSelectableRawValue: typeof SurveyDetailService.prototype.onChangeRequireSelectableRawValue;
  formatFileResourceURL: typeof SurveyDetailService.prototype.formatFileResourceURL;
  onFileBulkDownload: typeof SurveyDetailService.prototype.onFileBulkDownload;
  onDeleteSurveyAnswers: typeof SurveyDetailService.prototype.onDeleteSurveyAnswers;
  csvEncoding: CSVEncoding;
  onChangeCSVEncoding: typeof SurveyDetailService.prototype.onChangeCSVEncoding;
  resetCSVModalState: typeof SurveyDetailService.prototype.resetCSVModalState;
  onOpenPreview: typeof SurveyDetailService.prototype.onOpenPreview;
};

const defaultCSVModalState: Pick<
  SurveyDetailServiceProps,
  "csvEncoding" | "selectedMetaIds" | "requireSelectableRawValue"
> = {
  selectedMetaIds: csvMetaFields.map((field) => field.id),
  csvEncoding: "Shift_JIS",
  requireSelectableRawValue: false,
} as const;

export class SurveyDetailService extends BaseApplicationService<SurveyDetailServiceProps> {
  private readonly surveyAnswerCountPerPage: number = DefaultNumItemsPerPages;

  state: SurveyDetailServiceProps = {
    survey: undefined as SurveyEntity | undefined,
    tabState: SurveyTabState.initialize(),
    switchTab: this.switchTab.bind(this),
    results: [],
    tableData: this.tableData.bind(this),
    setDateRange: this.setDateRange.bind(this),
    dateRange: {},
    paging: {
      current: 1,
      total: 0,
    },
    onMount: this.onMount.bind(this),
    onSave: this.onSave.bind(this),
    onDelete: this.onDelete.bind(this),
    onUnPublish: this.onUnPublish.bind(this),
    onResumeAccepting: this.onResumeAccepting.bind(this),
    onPauseAccepting: this.onPauseAccepting.bind(this),
    onPublishSettingSave: this.onPublishSettingSave.bind(this),
    onResultsRequested: this.onResultsRequested.bind(this),
    onCSVDownload: this.onCSVDownload.bind(this),
    onGroupUpdated: this.onGroupUpdated.bind(this),
    onReturnToDraft: this.onReturnToDraft.bind(this),
    onCopy: this.onCopy.bind(this),
    onExportSurvey: this.onExportSurvey.bind(this),
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    onSelectCsvMeta: this.onSelectCsvMeta.bind(this),
    onChangeRequireSelectableRawValue:
      this.onChangeRequireSelectableRawValue.bind(this),
    formatFileResourceURL: this.formatFileResourceURL.bind(this),
    onFileBulkDownload: this.onFileBulkDownload.bind(this),
    onDeleteSurveyAnswers: this.onDeleteSurveyAnswers.bind(this),
    onChangeCSVEncoding: this.onChangeCSVEncoding.bind(this),
    resetCSVModalState: this.resetCSVModalState.bind(this),
    onOpenPreview: this.onOpenPreview.bind(this),
    ...defaultCSVModalState,
  };

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  async onMount(id: string, tab?: string) {
    try {
      const { data: survey } = await surveyApiClient.fetchSurvey(id);
      const { data: stats } = await surveyApiClient.fetchSurveyStats(id);
      this.setState({
        survey: survey,
        stats: stats,
        tabState: SurveyTabState.isValid(tab)
          ? SurveyTabState.from(tab)
          : SurveyTabState.initialize(),
      });
    } catch (e) {
      if (SmartApplyApiError.is404(e)) {
        // this.history.push("/404");
        return;
      }
      reportUnexpectedError(e);
      // this.history.push("/500");
      return;
    }
  }

  async onSave(entity: SurveyEntity) {
    try {
      const { data: updated } = await surveyApiClient.updateSurvey(
        entity.id,
        entity.name,
        entity.draft.formSchema,
        entity.isExcludeFromSearch,
        entity.isNotifyArrival,
        entity.customPath,
      );
      this.setState({
        survey: updated,
      });
      toast.success("アンケート設定を更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        toast.error("ネットワークエラーが発生しました。リトライしてください。");
        return;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期しないエラーが発生しました。しばらく時間をおいてからお試しください。",
      );
      return;
    }
  }

  async onResultsRequested() {
    try {
      const { data: results, headers } =
        await surveyApiClient.fetchSurveyAnswers(
          this.state.survey!.id,
          this.state.paging.current,
          this.surveyAnswerCountPerPage,
        );
      this.setState({
        results: results,
        paging: {
          ...this.state.paging,
          total: toTotalPage(
            Number(headers["pagination-count"]),
            this.surveyAnswerCountPerPage,
          ),
        },
      });
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("アンケートの回答結果取得に失敗しました。");
    }
  }

  async onReturnToDraft() {
    try {
      const res = await surveyApiClient.returnToDraftSurvey(
        this.state.survey!.id,
      );
      this.setState({ survey: res.data });
      toast.success("下書きに戻しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("下書きへの更新に失敗しました。");
    }
  }

  async onUnPublish() {
    try {
      const { data: survey } = await surveyApiClient.unpublishSurvey(
        this.state.survey!.id,
      );
      this.setState({
        survey,
      });
      toast.success("アンケートの公開を終了しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        toast.error("ネットワークエラーが発生しました。リトライしてください。");
        return;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて、再度お試しください。",
      );
      return;
    }
  }

  async onResumeAccepting() {
    try {
      const { data: survey } = await surveyApiClient.resumeAcceptingSurvey(
        this.state.survey!.id,
      );
      this.setState({
        survey,
      });
      toast.success("アンケートを受付再開にしました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        toast.error("ネットワークエラーが発生しました。リトライしてください。");
        return;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて、再度お試しください。",
      );
      return;
    }
  }
  async onPauseAccepting() {
    try {
      const { data: survey } = await surveyApiClient.pauseAcceptingSurvey(
        this.state.survey!.id,
      );
      this.setState({
        survey,
      });
      toast.success("アンケートを一時受付停止にしました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        toast.error("ネットワークエラーが発生しました。リトライしてください。");
        return;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて、再度お試しください。",
      );
      return;
    }
  }

  async onCopy(newName: string) {
    try {
      const { data: survey } = await surveyApiClient.copySurvey(
        this.state.survey!.id,
        newName,
      );
      this.history.push(SurveyR.SurveyResults.link({ id: survey.id }));
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        toast.error("ネットワークエラーが発生しました。リトライしてください。");
        return;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて、再度お試しください。",
      );
      return;
    }
  }
  async onExportSurvey() {
    if (this.state.survey === undefined) return;
    try {
      const { data } = await surveyApiClient.exportEncryptedSurveySettings(
        this.state.survey.id,
      );
      FileSaver.saveAs(new Blob([data]), `${this.state.survey.name}.zip`);
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("アンケートのエクスポートに失敗しました");
    }
  }

  async onDelete() {
    const survey: SurveyEntity = this.state.survey!;
    try {
      await surveyApiClient.deleteSurvey(survey.id);
      toast.success("アンケートを削除しました");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("アンケートの削除に失敗しました");
    }
  }

  async onPublishSettingSave(
    setting: SurveyPublishSetting,
    withPublish: boolean,
    isPublishNow: boolean,
    isStartAcceptAtPublish: boolean,
  ) {
    try {
      const { data: survey } = await surveyApiClient.updateSurveyPublishSetting(
        this.state.survey!.id,
        isPublishNow,
        isStartAcceptAtPublish,
        setting,
      );
      if (withPublish) {
        const { data: survey } = await surveyApiClient.publishSurvey(
          this.state.survey!.id,
        );
        this.setState({
          survey,
        });
        toast.success("アンケートの設定を確定しました。");
      } else {
        this.setState({
          survey,
        });
        toast.success("公開・受付日時を更新しました。");
      }
    } catch (e) {
      if (SmartApplyApiError.isVersionOutdated(e)) {
        toast.error(
          "アンケートの更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      ToastMessage.enexpectedError().show();
      reportUnexpectedError(e);
    }
  }

  async onCSVDownload() {
    const dates: { from?: Date; to?: Date } = this.state.dateRange;
    try {
      const { data } = await surveyApiClient.downloadSurveyAnswersCSV(
        this.state.survey!.id,
        {
          dateFrom: dates.from ? formatDate(dates.from) : undefined,
          dateTo: dates.to ? formatDate(dates.to) : undefined,
        },
        this.state.selectedMetaIds,
        this.state.requireSelectableRawValue,
        this.state.csvEncoding,
      );
      FileSaver.saveAs(
        data,
        nameCsvFileWithTerm(this.state.survey!.id, {
          from: dates.from,
          to: dates.to,
        }),
      );
    } catch (e) {
      // TODO use "bad_request.too_large_record_count" code
      if (SmartApplyApiError.isBadRequest(e)) {
        toast.error(
          "対象の申請件数が出力上限を超えました。指定期間を狭めてください。",
        );
        return;
      }
      reportUnexpectedError(e);
      toast.error("CSVダウンロードでエラーが発生しました");
    }
  }

  async onGroupUpdated(groupId: string) {
    try {
      const { data } = await surveyApiClient.updateGroupToSurvey(
        this.state.survey!.id,
        groupId,
      );
      this.setState({
        survey: data,
      });
      toast.success("管理グループを更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        toast.error("ネットワークエラーが発生しました。リトライしてください。");
        return;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて、再度お試しください。",
      );
      return;
    }
  }

  switchTab(tab: SurveyTabState) {
    this.history.push(rebuildQuery({ tab: tab.getValue() }));
    this.setState({
      tabState: tab,
    });
  }

  tableData(): {
    header: SurveyAnswerTableHeader;
    body: SurveyAnswerTableBody[];
  } {
    const result = this.state.results;
    const formSchema = this.state.survey!.draft!.formSchema;

    return generateSurveyAnswerTable(formSchema, result);
  }

  setDateRange(range: { from?: Date; to?: Date }) {
    this.setState({
      dateRange: range,
    });
  }

  onSelectCsvMeta(ids: SurveyCsvMetaID[]) {
    this.setState({
      selectedMetaIds: ids,
    });
  }

  onChangeRequireSelectableRawValue(isRequired: boolean) {
    this.setState({
      requireSelectableRawValue: isRequired,
    });
  }

  formatFileResourceURL(answerId: string, refKey: string) {
    return encodeURI(
      `/files/survey-answers/${answerId}/attached-files/${refKey}`,
    );
  }

  async onFileBulkDownload() {
    const results = this.state.results;
    const formSchema = this.state.survey!.published!.formSchema;
    try {
      const folders = await generateAttachedFilesFolder(formSchema, results);
      const zip = await generateZip(folders, { encodeShiftJis: true });
      FileSaver.saveAs(zip, `${this.state.survey!.id}.zip`);
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return;
      }
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      reportUnexpectedError(e);
    }
  }

  async onDeleteSurveyAnswers() {
    try {
      const result = await runGqlQueryV2(() =>
        gqlClient.removeSurveyAnswer(
          { surveyId: this.state.survey!.id },
          gqlHeader("m"),
        ),
      );
      if (result.error) {
        throw result.error;
      }
      await this.onResultsRequested();
      toast.success("アンケートデータを削除しました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("アンケートデータの削除に失敗しました。");
      return;
    }
  }

  onChangeCSVEncoding(encoding: CSVEncoding) {
    this.setState({ csvEncoding: encoding });
  }
  resetCSVModalState() {
    this.setState({ ...defaultCSVModalState });
  }

  async onOpenPreview(orgSlug: string) {
    const { data } = await surveyApiClient.generateSurveyPreviewToken(
      this.state.survey!.id,
    );
    const previewLink = this.state.survey!.previewURL(
      orgSlug,
      data.previewToken,
      isLGWANURL(window.location.href),
    );

    window.open(previewLink, "_blank");
  }
}
